/**
 * Avatar por viseme: o mapa das bocas, a cadência e o agendador.
 *
 * ESTE ARQUIVO EXISTE EM DOIS LUGARES, BYTE A BYTE IGUAIS:
 *
 *   - modules/platform/app-shared/avatar/visemes.js   (canônico)
 *   - modules/eia/app/src/utils/visemes.js            (cópia obrigatória)
 *
 * A cópia existe porque o app do eia é Create React App sem craco: o
 * `ModuleScopePlugin` recusa import de fora de `src/`, então não há como ele
 * consumir o compartilhado por caminho relativo. A divergência entre as duas
 * cópias é pega pelo teste `avatar-visemes.test.mjs`, que compara os arquivos
 * caractere a caractere e falha se diferirem. **Editar uma e esquecer a outra
 * fica vermelho na suíte**, que é o ponto.
 *
 * POR QUE ISSO FOI EXTRAÍDO, em 04/10/2026. A mesma técnica — compor a boca na
 * hora sobre uma foto, a partir dos visemes que a Azure devolve com o áudio —
 * estava implementada duas vezes: no player de entrevista do eia (React) e no
 * palco de aula da capacitação (Vue). Não havia import entre os dois; a
 * dependência era um COMENTÁRIO. E as nove classes de boca estavam traduzidas
 * de um lado para o outro (`closed`/`fechada`, `smile`/`sorriso`), então nem um
 * diff mecânico acusava diferença.
 *
 * E já tinham divergido: a retenção mínima e o intervalo batiam, mas o
 * crossfade de 35 ms existia só no eia. Na aula a boca trocava em corte seco.
 *
 * OS NOMES CANÔNICOS SÃO EM PORTUGUÊS, como o resto de `app-shared`. O acervo
 * de fotos do eia foi gerado com chaves em inglês por `extrair-acervo-de-boca`,
 * e renomear arquivo de acervo publicado não vale o risco — por isso existe
 * `EM_INGLES`, que traduz na borda, e só ali.
 */

/** Os 22 IDs oficiais de viseme da Azure Neural TTS, agrupados nas 9 classes
 *  visualmente distintas do acervo de fotos de boca (estilo Preston Blair). */
export const VISEME_POR_ID = {
  0: 'fechada',
  1: 'consoante',
  2: 'consoante',
  3: 'abertoA',
  4: 'abertoA',
  5: 'abertoA',
  6: 'fechadaEstreita',
  7: 'arredondadoU',
  8: 'arredondadoO',
  9: 'arredondadoO',
  10: 'consoante',
  11: 'consoante',
  12: 'consoante',
  13: 'consoante',
  14: 'ele',
  15: 'consoante',
  16: 'consoante',
  17: 'consoante',
  18: 'efe',
  19: 'fechadaEstreita',
  20: 'fechadaEstreita',
  21: 'sorriso',
};

/** A boca em repouso. Vale como começo e como queda em qualquer erro. */
export const BOCA_PADRAO = 'fechada';

export const BOCAS = [
  'fechada', 'fechadaEstreita', 'consoante', 'ele', 'efe',
  'sorriso', 'abertoA', 'arredondadoO', 'arredondadoU',
];

/** Tradução para as chaves do acervo de fotos do eia, geradas em inglês. */
export const EM_INGLES = {
  fechada: 'closed',
  fechadaEstreita: 'closedNarrow',
  consoante: 'consonant',
  ele: 'l',
  efe: 'fv',
  sorriso: 'smile',
  abertoA: 'openA',
  arredondadoO: 'roundedO',
  arredondadoU: 'roundedU',
};

/**
 * A cadência, validada em produção e medida contra o spike original.
 *
 * `retencaoMinimaMs` é o que separa fala de tremor: a cadência real de fonemas
 * da Azure chega a trocar a cada 50-80 ms, e aplicada crua a boca vibra.
 * `intervaloMs` é amostragem por relógio do áudio — mais robusta contra engasgo
 * de buffer do que agendar cada troca. `crossfadeMs` é a mistura entre uma boca
 * e a seguinte; sem ela a troca é corte seco e o rosto endurece.
 */
export const CADENCIA = {
  retencaoMinimaMs: 110,
  intervaloMs: 25,
  crossfadeMs: 35,
};

export function nomeDoViseme(id) {
  return VISEME_POR_ID[id] || BOCA_PADRAO;
}

/**
 * Agendador de troca de boca sincronizado a um áudio em reprodução.
 *
 * Não conhece React nem Vue, não toca no DOM e não guarda elemento de áudio:
 * recebe uma função que diz o tempo atual e avisa por retorno quando a boca
 * muda. Quem desenha é de quem chama.
 *
 * @param {Object} opts
 * @param {() => number} opts.tempoAtualMs  tempo do áudio em reprodução, em ms.
 * @param {Array<{visemeId:number, offsetMs:number}>} opts.visemes eventos da Azure.
 * @param {(nome: string) => void} opts.aoTrocar chamado a cada troca aceita.
 */
export function criarAgendador({ tempoAtualMs, visemes, aoTrocar }) {
  let timer = null;
  let atual = BOCA_PADRAO;
  let desde = 0;
  let ultimo = -1;

  const ordenados = Array.isArray(visemes)
    ? [...visemes].sort((a, b) => a.offsetMs - b.offsetMs)
    : [];

  function passo() {
    const agora = tempoAtualMs();

    let i = ultimo;
    while (i + 1 < ordenados.length && ordenados[i + 1].offsetMs <= agora) i += 1;
    if (i < 0) return;
    ultimo = i;

    const proxima = nomeDoViseme(ordenados[i].visemeId);
    if (proxima === atual) return;
    if (agora - desde < CADENCIA.retencaoMinimaMs) return;

    atual = proxima;
    desde = agora;
    aoTrocar(proxima);
  }

  return {
    comecar() {
      ultimo = -1;
      atual = BOCA_PADRAO;
      desde = 0;
      aoTrocar(BOCA_PADRAO);
      timer = setInterval(passo, CADENCIA.intervaloMs);
    },
    parar() {
      if (timer) clearInterval(timer);
      timer = null;
    },
  };
}
